/**
 * SourceApplication Types for MEV-Med Frontend
 * Type definitions for source generator application management across organizational levels
 *
 * @version 2025.1.0
 * @description Tipos TypeScript para gestao de aplicacoes de fontes geradoras em 4 niveis organizacionais
 */

// ============================================================================
// ENUMS
// ============================================================================

/**
 * Application levels in the organizational hierarchy
 */
export enum ApplicationLevel {
  COMPANY = 'company',
  DEPARTMENT = 'department',
  OCCUPATION = 'occupation',
  EMPLOYEE = 'employee',
}

/**
 * Exposure level for source generator applications
 */
export enum SourceExposureLevel {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  VERY_HIGH = 'VERY_HIGH',
}

// ============================================================================
// APPLICATION DATA INTERFACES
// ============================================================================

/**
 * Data for creating/updating a source generator application
 * Used in apply and update operations
 */
export interface SourceApplicationData {
  exposureLevel?: SourceExposureLevel;
  startDate?: string | null;
  endDate?: string | null;
  observations?: string | null;
}

/**
 * Request payload for applying a source generator
 */
export interface ApplySourceRequest {
  sourceGeneratorId: number;
  exposureLevel?: SourceExposureLevel;
  startDate?: string | null;
  endDate?: string | null;
  observations?: string | null;
}

/**
 * Request payload for applying multiple source generators
 */
export interface ApplyMultipleSourcesRequest {
  sourceGeneratorIds: number[];
  exposureLevel?: SourceExposureLevel;
  startDate?: string | null;
  endDate?: string | null;
  observations?: string | null;
}

/**
 * Request payload for updating an application
 */
export interface UpdateSourceApplicationRequest {
  exposureLevel?: SourceExposureLevel;
  startDate?: string | null;
  endDate?: string | null;
  observations?: string | null;
}

// ============================================================================
// SOURCE WITH APPLICATION (Applied Sources)
// ============================================================================

/**
 * Source generator with application details
 * Represents a source generator that has been applied to a target
 */
export interface SourceWithApplication {
  id: number;
  sourceGeneratorId: number;
  sourceName: string;
  riskType: string;
  location: string | null;
  exposureLevel: SourceExposureLevel;
  startDate?: string | null;
  endDate?: string | null;
  observations?: string | null;
  isActive: boolean;
  isCritical: boolean;
  exposureDays: number;
  applicationLevel: ApplicationLevel;
  createdAt: string;
  updatedAt: string;
}

/**
 * Check if a source application is active (no endDate or endDate in future)
 */
export function isSourceActive(source: SourceWithApplication): boolean {
  return source.isActive;
}

/**
 * Check if a source is critical (HIGH or VERY_HIGH exposure)
 */
export function isSourceCritical(source: SourceWithApplication): boolean {
  return source.isCritical;
}

// ============================================================================
// AVAILABLE SOURCE (Not Yet Applied)
// ============================================================================

/**
 * Available source generator for application
 * Represents a source generator that can be applied to a target
 */
export interface AvailableSource {
  id: number;
  name: string;
  riskType: string;
  location: string | null;
}

// ============================================================================
// CRITICAL EXPOSURE ALERTS
// ============================================================================

/**
 * Critical exposure alert
 */
export interface CriticalSourceExposure {
  applicationId: number;
  sourceGeneratorId: number;
  sourceName: string;
  targetId: number;
  targetType: ApplicationLevel;
  targetName: string;
  exposureLevel: SourceExposureLevel;
  exposureDays: number;
  startDate: string;
}

/**
 * Critical exposure alerts response with summary
 */
export interface CriticalSourceExposureAlerts {
  critical: CriticalSourceExposure[];
  summary: {
    totalCritical: number;
    veryHighCount: number;
    highCount: number;
  };
}

// ============================================================================
// API RESPONSE INTERFACES
// ============================================================================

/**
 * Response for get sources endpoint (applied + available)
 */
export interface SourcesForTargetResponse {
  applied: SourceWithApplication[];
  available: AvailableSource[];
}

/**
 * Generic API success response
 */
export interface SourceApplicationSuccessResponse {
  message: string;
  data?: {
    sourceGeneratorId?: number;
    level?: string;
    targetId?: number;
    success?: number[];
    failed?: Array<{ sourceGeneratorId: number; error: string }>;
  };
}

/**
 * API error response
 */
export interface SourceApplicationErrorResponse {
  error: string;
  code: string;
}

// ============================================================================
// TARGET INFORMATION
// ============================================================================

/**
 * Target entity for application context
 */
export interface ApplicationTarget {
  id: number;
  name: string;
  level: ApplicationLevel;
  parentId?: number;
  parentName?: string;
}

/**
 * Hierarchical target with full path
 */
export interface ApplicationTargetWithPath extends ApplicationTarget {
  companyId: number;
  companyName: string;
  departmentId?: number;
  departmentName?: string;
  occupationId?: number;
  occupationName?: string;
}

// ============================================================================
// UI COMPONENT PROPS
// ============================================================================

/**
 * Props for DualTransferList component (source version)
 */
export interface SourceTransferListProps {
  applied: SourceWithApplication[];
  available: AvailableSource[];
  onApply: (sourceGeneratorIds: number[], data?: SourceApplicationData) => Promise<void>;
  onRemove: (sourceGeneratorIds: number[]) => Promise<void>;
  loading?: boolean;
  disabled?: boolean;
}

/**
 * Props for SourceApplicationModal (for setting exposure details)
 */
export interface SourceApplicationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (data: SourceApplicationData) => Promise<void>;
  source: AvailableSource | SourceWithApplication | null;
  mode: 'apply' | 'edit';
  loading?: boolean;
}

/**
 * Props for ExposureLevelBadge component
 */
export interface SourceExposureLevelBadgeProps {
  level: SourceExposureLevel;
  showLabel?: boolean;
}

/**
 * Props for CriticalAlertsTable component
 */
export interface CriticalSourceAlertsTableProps {
  alerts: CriticalSourceExposureAlerts;
  onAlertClick?: (alert: CriticalSourceExposure) => void;
  loading?: boolean;
}

// ============================================================================
// FILTER INTERFACES
// ============================================================================

/**
 * Filters for source applications list
 */
export interface SourceApplicationFilters {
  targetLevel?: ApplicationLevel;
  targetId?: number;
  exposureLevel?: SourceExposureLevel;
  isActive?: boolean;
  isCritical?: boolean;
  search?: string;
}

/**
 * Filters for critical alerts
 */
export interface CriticalSourceAlertsFilters {
  companyId: number;
  targetType?: ApplicationLevel;
  exposureLevel?: SourceExposureLevel;
}

// ============================================================================
// LABELS AND CONSTANTS
// ============================================================================

/**
 * Labels for application levels (Portuguese)
 */
export const ApplicationLevelLabels: Record<ApplicationLevel, string> = {
  [ApplicationLevel.COMPANY]: 'Empresa',
  [ApplicationLevel.DEPARTMENT]: 'Setor',
  [ApplicationLevel.OCCUPATION]: 'Cargo',
  [ApplicationLevel.EMPLOYEE]: 'Funcionario',
};

/**
 * Labels for exposure levels (Portuguese)
 */
export const SourceExposureLevelLabels: Record<SourceExposureLevel, string> = {
  [SourceExposureLevel.LOW]: 'Baixo',
  [SourceExposureLevel.MEDIUM]: 'Medio',
  [SourceExposureLevel.HIGH]: 'Alto',
  [SourceExposureLevel.VERY_HIGH]: 'Muito Alto',
};

/**
 * Colors for exposure level badges
 */
export const SourceExposureLevelColors: Record<SourceExposureLevel, 'success' | 'info' | 'warning' | 'error'> = {
  [SourceExposureLevel.LOW]: 'success',
  [SourceExposureLevel.MEDIUM]: 'info',
  [SourceExposureLevel.HIGH]: 'warning',
  [SourceExposureLevel.VERY_HIGH]: 'error',
};

/**
 * Risk type labels (Portuguese)
 */
export const RiskTypeLabels: Record<string, string> = {
  PHYSICAL: 'Fisico',
  CHEMICAL: 'Quimico',
  BIOLOGICAL: 'Biologico',
  ERGONOMIC: 'Ergonomico',
  ACCIDENT: 'Acidente',
  OTHER: 'Outro',
};

/**
 * Risk type colors
 */
export const RiskTypeColors: Record<string, string> = {
  PHYSICAL: '#2196F3',
  CHEMICAL: '#9C27B0',
  BIOLOGICAL: '#4CAF50',
  ERGONOMIC: '#FF9800',
  ACCIDENT: '#F44336',
  OTHER: '#607D8B',
};

// ============================================================================
// UTILITY TYPES
// ============================================================================

/**
 * Type guard for checking if a source is applied
 */
export function isSourceWithApplication(
  source: SourceWithApplication | AvailableSource
): source is SourceWithApplication {
  return 'applicationLevel' in source && 'exposureLevel' in source;
}

/**
 * Format exposure days for display
 */
export function formatExposureDays(days: number): string {
  if (days === 0) {
    return 'Hoje';
  }
  if (days === 1) {
    return '1 dia';
  }
  if (days < 30) {
    return `${days} dias`;
  }
  const months = Math.floor(days / 30);
  if (months === 1) {
    return '1 mes';
  }
  if (months < 12) {
    return `${months} meses`;
  }
  const years = Math.floor(months / 12);
  if (years === 1) {
    return '1 ano';
  }
  return `${years} anos`;
}

/**
 * Get exposure level from string (case insensitive)
 */
export function parseSourceExposureLevel(level: string): SourceExposureLevel {
  const normalized = level.toUpperCase();
  if (normalized in SourceExposureLevel) {
    return normalized as SourceExposureLevel;
  }
  return SourceExposureLevel.MEDIUM;
}

/**
 * Sort sources by exposure level (most critical first)
 */
export function sortByExposureLevel(sources: SourceWithApplication[]): SourceWithApplication[] {
  const order: Record<SourceExposureLevel, number> = {
    [SourceExposureLevel.VERY_HIGH]: 0,
    [SourceExposureLevel.HIGH]: 1,
    [SourceExposureLevel.MEDIUM]: 2,
    [SourceExposureLevel.LOW]: 3,
  };

  return [...sources].sort((a, b) => {
    return order[a.exposureLevel] - order[b.exposureLevel];
  });
}

/**
 * Group sources by risk type
 */
export function groupByRiskType(
  sources: (SourceWithApplication | AvailableSource)[]
): Record<string, (SourceWithApplication | AvailableSource)[]> {
  const grouped: Record<string, (SourceWithApplication | AvailableSource)[]> = {};

  for (const source of sources) {
    const type = source.riskType ?? 'OTHER';

    if (!grouped[type]) {
      grouped[type] = [];
    }
    grouped[type].push(source);
  }

  return grouped;
}
